/**
 * One place to ask "how old is this, and who is fetching it".
 *
 * The four sources fetch in four different providers — the briefing inside the
 * Formular, the winds and the terrain around the whole editor, the openAIP feed
 * in the editor itself — but the pilot reads their state in ONE shape, in the
 * header of the section the data lands in. This is the little bus between them:
 * each provider publishes `{busy, error, at, refresh}` for its family, and
 * `<Freshness family="…">` renders whatever is there.
 *
 * It carries no data and no provenance. What the value IS lives in the form's
 * cells; where it comes from lives behind the ⓘ.
 *
 * Outside a {@link FreshnessProvider} — the shared read-only page, which fetches
 * nothing — every family reads null and the widget renders nothing at all.
 */
import {
  createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode,
} from 'react';

/** Wetter · Wind · Gelände · Platzdaten — the four sources that reach the network. */
export type Family = 'wx' | 'winds' | 'terrain' | 'geo';

export const FAMILIES: readonly Family[] = ['wx', 'winds', 'terrain', 'geo'];

export interface FamilyState {
  busy: boolean;
  /** the last failure, still unresolved; the widget says so and the loader retries */
  error: unknown;
  /** epoch ms of the last successful fetch in this session, or null */
  at: number | null;
  refresh(): void;
}

class FreshnessBus {
  private byFamily = new Map<Family, FamilyState>();
  private subs = new Map<Family, Set<() => void>>();

  publish(family: Family, state: FamilyState | null): void {
    const prev = this.byFamily.get(family) ?? null;
    if (prev === state) return;
    if (
      prev &&
      state &&
      prev.busy === state.busy &&
      prev.error === state.error &&
      prev.at === state.at &&
      prev.refresh === state.refresh
    ) {
      return;
    }
    if (state) this.byFamily.set(family, state);
    else this.byFamily.delete(family);
    for (const cb of this.subs.get(family) ?? []) cb();
  }

  subscribe = (family: Family) => (cb: () => void): (() => void) => {
    let set = this.subs.get(family);
    if (!set) {
      set = new Set();
      this.subs.set(family, set);
    }
    set.add(cb);
    return () => {
      set?.delete(cb);
    };
  };

  /** A stable snapshot: the same object until the family publishes a new one. */
  get = (family: Family) => (): FamilyState | null => this.byFamily.get(family) ?? null;
}

const Ctx = createContext<FreshnessBus | null>(null);

export function FreshnessProvider({ children }: { children: ReactNode }) {
  const bus = useMemo(() => new FreshnessBus(), []);
  return <Ctx.Provider value={bus}>{children}</Ctx.Provider>;
}

/**
 * A source says how it is doing. `state` must be memoised by the caller — it is
 * compared field by field, so a fresh object with identical contents is free.
 */
export function usePublishFreshness(family: Family, state: FamilyState | null): void {
  const bus = useContext(Ctx);
  useEffect(() => {
    if (!bus) return;
    bus.publish(family, state);
    return () => bus.publish(family, null);
  }, [bus, family, state]);
}

/** What one family is doing; null where nothing fetches (the shared view). */
export function useFamily(family: Family): FamilyState | null {
  const bus = useContext(Ctx);
  const subscribe = useMemo(
    () => (bus ? bus.subscribe(family) : () => () => undefined),
    [bus, family],
  );
  const snapshot = useMemo(() => (bus ? bus.get(family) : () => null), [bus, family]);
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
